import {
  computeA4PageCount,
  exportCanvasToPdf,
  renderElementToCanvas,
  type ExportPdfSettings,
} from "../io/exportRenderedPdf.js";

/**
 * Modal: preview capture, layout (single tall page vs A4 split), orientation, and page range — then download PDF.
 * @returns whether a file was saved (false if cancelled).
 */
export function openExportPdfPreviewDialog(opts: {
  element: HTMLElement;
  filenameStem: string;
}): Promise<boolean> {
  return new Promise((resolve) => {
    const dlg = document.createElement("dialog");
    dlg.className = "npp-dialog npp-dialog-search npp-dialog-export-pdf";

    dlg.innerHTML = `
      <div class="npp-dialog-form npp-dialog-search-form">
        <h2 class="npp-dialog-title">Export to PDF</h2>
        <p class="npp-dialog-hint">Preview (approximate colours / fonts). Width matches the exported capture.</p>
        <div class="npp-export-pdf-preview-wrap" data-role="preview-wrap">
          <p class="npp-export-pdf-status" data-role="status">Rendering preview…</p>
          <img class="npp-export-pdf-preview-img" alt="PDF preview" data-role="preview" hidden />
        </div>
        <div class="npp-dialog-row npp-export-pdf-row">
          <label class="npp-export-pdf-label" for="npp-export-pdf-layout">Layout</label>
          <div class="npp-export-pdf-field">
            <select id="npp-export-pdf-layout" class="npp-dialog-input" data-role="layout">
              <option value="fit">Single PDF page (full document)</option>
              <option value="a4">Multiple A4 pages (split vertically)</option>
            </select>
          </div>
        </div>
        <div class="npp-dialog-row npp-export-pdf-row npp-export-pdf-a4-row" data-role="a4-only" hidden>
          <label class="npp-export-pdf-label" for="npp-export-pdf-orient">Orientation</label>
          <div class="npp-export-pdf-field">
            <select id="npp-export-pdf-orient" class="npp-dialog-input" data-role="orient">
              <option value="portrait">Portrait</option>
              <option value="landscape">Landscape</option>
            </select>
          </div>
        </div>
        <div class="npp-dialog-row npp-export-pdf-row npp-export-pdf-a4-row" data-role="a4-only" hidden>
          <span class="npp-export-pdf-label">Pages</span>
          <div class="npp-export-pdf-pages">
            <label class="npp-export-pdf-sublabel">From <input type="number" min="1" value="1" class="npp-dialog-input npp-export-pdf-num" data-role="from" /></label>
            <label class="npp-export-pdf-sublabel">To <input type="number" min="1" value="" class="npp-dialog-input npp-export-pdf-num" data-role="to" placeholder="All" /></label>
            <span class="npp-dialog-hint npp-export-pdf-total" data-role="total"></span>
          </div>
        </div>
        <div class="npp-dialog-actions">
          <button type="button" data-action="export">Export</button>
          <button type="button" data-action="cancel">Cancel</button>
        </div>
      </div>`;

    document.body.append(dlg);

    const statusEl = dlg.querySelector<HTMLElement>('[data-role="status"]')!;
    const previewImg = dlg.querySelector<HTMLImageElement>('[data-role="preview"]')!;
    const layoutSel = dlg.querySelector<HTMLSelectElement>('[data-role="layout"]')!;
    const orientSel = dlg.querySelector<HTMLSelectElement>('[data-role="orient"]')!;
    const fromIn = dlg.querySelector<HTMLInputElement>('[data-role="from"]')!;
    const toIn = dlg.querySelector<HTMLInputElement>('[data-role="to"]')!;
    const totalEl = dlg.querySelector<HTMLElement>('[data-role="total"]')!;
    const a4Rows = [...dlg.querySelectorAll<HTMLElement>("[data-role=\"a4-only\"]")];
    const captureMode = "viewport-biased" as const;
    const exportBtn = dlg.querySelector<HTMLButtonElement>('[data-action="export"]')!;
    const cancelBtn = dlg.querySelector<HTMLButtonElement>('[data-action="cancel"]')!;

    let previewCanvas: HTMLCanvasElement | null = null;
    let closed = false;

    const close = (saved: boolean): void => {
      if (closed) return;
      closed = true;
      dlg.close();
      dlg.remove();
      resolve(saved);
    };

    const syncA4Visibility = (): void => {
      const a4 = layoutSel.value === "a4";
      for (const row of a4Rows) row.hidden = !a4;
    };

    const parsePageNum = (raw: string, fallback: number): number => {
      const t = raw.trim();
      if (t === "") return fallback;
      const v = parseInt(t, 10);
      return Number.isFinite(v) ? v : fallback;
    };

    /**
     * @param resetRange When true, set From=1 and To=last page (e.g. preview ready, switched to A4, or orientation changed).
     */
    const refreshPageBounds = (resetRange = false): void => {
      syncA4Visibility();
      if (layoutSel.value !== "a4" || !previewCanvas) {
        totalEl.textContent = "";
        return;
      }
      const orientation = orientSel.value === "landscape" ? "landscape" : "portrait";
      const n = computeA4PageCount(previewCanvas, orientation);
      fromIn.min = "1";
      fromIn.max = String(n);
      toIn.min = "1";
      toIn.max = String(n);

      if (resetRange) {
        fromIn.value = "1";
        toIn.value = String(n);
      } else {
        const f = Math.min(n, Math.max(1, parsePageNum(fromIn.value, 1)));
        const t = Math.min(n, Math.max(f, parsePageNum(toIn.value, n)));
        fromIn.value = String(f);
        toIn.value = String(t);
      }
      totalEl.textContent = `(${n} page${n === 1 ? "" : "s"} total)`;
    };

    const readSettings = (): ExportPdfSettings => {
      const layout: ExportPdfSettings["layout"] = layoutSel.value === "a4" ? "a4-multi" : "fit-content";
      const orientation = orientSel.value === "landscape" ? "landscape" : "portrait";
      let pageFrom = Math.max(1, parsePageNum(fromIn.value, 1));
      const nPages = previewCanvas && layout === "a4-multi" ? computeA4PageCount(previewCanvas, orientation) : 1;
      let pageTo = Math.max(1, parsePageNum(toIn.value, nPages));
      if (previewCanvas && layout === "a4-multi") {
        const n = nPages;
        pageFrom = Math.min(n, pageFrom);
        pageTo = Math.min(n, Math.max(pageFrom, pageTo));
      } else {
        pageFrom = 1;
        pageTo = 1;
      }
      return { layout, orientation, pageFrom, pageTo };
    };

    layoutSel.addEventListener("change", () => {
      refreshPageBounds(layoutSel.value === "a4");
    });
    const onOrientChange = (): void => {
      refreshPageBounds(true);
    };
    orientSel.addEventListener("change", onOrientChange);
    orientSel.addEventListener("input", onOrientChange);
    fromIn.addEventListener("change", () => refreshPageBounds(false));
    toIn.addEventListener("change", () => refreshPageBounds(false));

    cancelBtn.addEventListener("click", () => close(false));

    dlg.addEventListener("cancel", (e) => {
      e.preventDefault();
      close(false);
    });

    dlg.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close(false);
      }
    });

    exportBtn.addEventListener("click", () => {
      void (async () => {
        exportBtn.disabled = true;
        cancelBtn.disabled = true;
        statusEl.textContent = "Rendering at full quality…";
        try {
          const settings = readSettings();
          const scale = Math.min(2, window.devicePixelRatio || 1.5);
          const fullCanvas = await renderElementToCanvas(opts.element, scale, { captureWidthMode: captureMode });
          statusEl.textContent = "Building PDF…";
          await exportCanvasToPdf(fullCanvas, opts.filenameStem, settings);
          close(true);
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          void alert(`PDF export failed: ${msg}`);
          statusEl.textContent = "Export failed — adjust options or try a smaller region.";
          exportBtn.disabled = false;
          cancelBtn.disabled = false;
        }
      })();
    });

    void (async () => {
      try {
        const c = await renderElementToCanvas(opts.element, 0.72, { captureWidthMode: captureMode });
        previewCanvas = c;
        previewImg.src = c.toDataURL("image/png");
        previewImg.hidden = false;
        statusEl.hidden = true;
        refreshPageBounds(true);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        statusEl.textContent = `Preview failed: ${msg}`;
      }
    })();

    syncA4Visibility();
    dlg.showModal();
    layoutSel.focus();
  });
}
